// One role's Program Brochure: the active upload, its actions, and its history.
//
// Mounted TWICE (Buyer, Seller) by ProgramBrochurePage, which is why it is a
// component rather than page-local markup — the two panels must not drift, and
// the only difference between them is a role string.
//
// Modelled on PartnerBrandLogoControl, the closest existing upload surface: a
// hidden file input driven by a Replace/Upload button, `extractErrorMessage` on
// the mutation's rejection, and an inline `role="alert"` beneath the actions.
// What it adds is a history table with restore, and pre-flight validation — the
// logo control has none, because a 60 KB PNG rejected server-side costs nothing,
// whereas an oversized brochure is 5 MB up the wire to be told the type is wrong.

import { useRef, useState } from "react";
import { Download, FileText, RotateCcw, Trash2, Upload } from "lucide-react";
import type { PrequalificationRole } from "@/api/prequalification";
import {
  PROGRAM_BROCHURE_CONTENT_TYPE,
  describeInvalidBrochureFile,
  formatBrochureSize,
  programBrochuresApi,
  type ProgramBrochure,
  type ProgramBrochureRoleState,
} from "@/api/programBrochures";
import { extractErrorMessage } from "@/api/client";
import { fallbackUserLabel, useInternalUserLabels } from "@/api/internalUsers";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  StatusBadge,
  SubTable,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { useDateFormatter } from "@/utils/datetime";
import {
  useDeactivateProgramBrochure,
  useRestoreProgramBrochure,
  useUploadProgramBrochure,
} from "@/hooks/prequalification/useProgramBrochures";

/** `.pdf` sits beside the MIME type because some file pickers filter on the
 *  extension and some on the type; neither is a check — describeInvalidBrochureFile
 *  is, and it runs on whatever the picker hands back. */
const ACCEPT = `${PROGRAM_BROCHURE_CONTENT_TYPE},.pdf`;

export interface ProgramBrochurePanelProps {
  state: ProgramBrochureRoleState;
}

export function ProgramBrochurePanel({ state }: ProgramBrochurePanelProps) {
  const role = state.role;
  const lower = role.toLowerCase();
  const toast = useToast();
  const { dateTime } = useDateFormatter();

  const upload = useUploadProgramBrochure();
  const restore = useRestoreProgramBrochure();
  const deactivate = useDeactivateProgramBrochure();

  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  /** Set only when a popup blocker swallowed window.open — see openBrochure. */
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState<ProgramBrochure | null>(null);
  const [viewing, setViewing] = useState(false);

  // Resolve uploader ids to names in one batched read across the active row and
  // every history row — never a raw GUID.
  const uploaderIds = [state.active, ...state.history]
    .map((b) => b?.uploadedByUserId)
    .filter((id): id is string => Boolean(id));
  const { lookup } = useInternalUserLabels(uploaderIds);

  const uploaderName = (id: string | null) =>
    id ? (lookup(id)?.displayName ?? fallbackUserLabel(id)) : "Unknown";

  const busy = upload.isPending || restore.isPending || deactivate.isPending;

  const onPickFile = async (file: File) => {
    setError(null);
    setBlockedUrl(null);

    const invalid = await describeInvalidBrochureFile(file);
    if (invalid) {
      setError(invalid);
      return;
    }

    try {
      await upload.mutateAsync({ role, file });
      toast.success(`${role} brochure uploaded.`);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  /**
   * Fetch a fresh SAS and open it.
   *
   * The link is fetched at click time and never cached — a SAS expires, and a
   * stale one handed out of a query cache is a broken download. That makes
   * `window.open` a POST-AWAIT call, which popup blockers treat as unrequested,
   * so its null return is checked and the URL is offered as a real anchor
   * instead. A silently-swallowed open is the failure this codebase has shipped
   * before.
   */
  const openBrochure = async () => {
    setError(null);
    setBlockedUrl(null);
    setViewing(true);
    try {
      const link = await programBrochuresApi.link(role);
      if (!link.available || !link.url) {
        setError(`No ${lower} brochure is on file to view.`);
        return;
      }
      const opened = window.open(link.url, "_blank", "noopener");
      if (!opened) setBlockedUrl(link.url);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setViewing(false);
    }
  };

  const doRemove = async () => {
    setConfirmRemove(false);
    setError(null);
    try {
      await deactivate.mutateAsync({ role });
      toast.success(`${role} brochure withdrawn.`);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const doRestore = async (target: ProgramBrochure) => {
    setConfirmRestore(null);
    setError(null);
    try {
      await restore.mutateAsync({ role, id: target.id });
      toast.success(`${role} brochure restored.`);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const historyColumns: SubTableColumn<ProgramBrochure>[] = [
    {
      key: "file",
      header: "File",
      render: (b) => <span className="text-onyx">{b.originalFileName}</span>,
      searchText: (b) => b.originalFileName,
    },
    {
      key: "size",
      header: "Size",
      align: "right",
      render: (b) => formatBrochureSize(b.sizeBytes),
      sortAccessor: (b) => b.sizeBytes,
    },
    {
      key: "pages",
      header: "Pages",
      align: "right",
      // Null means the parser could not open the file, NOT zero pages.
      render: (b) => (b.pageCount === null ? <span className="text-gray-mid">—</span> : b.pageCount),
      sortAccessor: (b) => b.pageCount ?? null,
    },
    {
      key: "uploaded",
      header: "Uploaded",
      render: (b) => dateTime(b.uploadedAtUtc),
      sortAccessor: (b) => b.uploadedAtUtc,
    },
    {
      key: "by",
      header: "By",
      render: (b) => uploaderName(b.uploadedByUserId),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (b) => (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => setConfirmRestore(b)}
        >
          <RotateCcw size={14} strokeWidth={1.5} aria-hidden /> Restore
        </Button>
      ),
    },
  ];

  return (
    <Card data-testid={`brochure-panel-${lower}`}>
      <CardHeader className="flex-row items-center justify-between gap-space-3">
        <CardTitle>{role} brochure</CardTitle>
        {state.active ? (
          <StatusBadge tone="ok">Live</StatusBadge>
        ) : (
          <StatusBadge tone="warn">Not set</StatusBadge>
        )}
      </CardHeader>

      {state.active ? (
        <ActiveBrochure
          brochure={state.active}
          uploadedBy={uploaderName(state.active.uploadedByUserId)}
          uploadedAt={dateTime(state.active.uploadedAtUtc)}
        />
      ) : (
        <NoBrochure role={role} />
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        aria-label={`${role} brochure file`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          // Reset first: re-picking the SAME file must fire another change event.
          e.target.value = "";
          if (f) void onPickFile(f);
        }}
      />

      <div className="mt-space-3 flex flex-wrap items-center gap-space-2">
        {state.active && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void openBrochure()}
            loading={viewing}
          >
            <Download size={14} strokeWidth={1.5} aria-hidden /> View
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant={state.active ? "secondary" : "primary"}
          onClick={() => inputRef.current?.click()}
          loading={upload.isPending}
          disabled={busy && !upload.isPending}
        >
          <Upload size={14} strokeWidth={1.5} aria-hidden />
          {state.active ? "Replace" : "Upload a PDF"}
        </Button>
        {state.active && (
          <Button
            type="button"
            size="sm"
            variant="text"
            onClick={() => setConfirmRemove(true)}
            loading={deactivate.isPending}
            disabled={busy && !deactivate.isPending}
          >
            <Trash2 size={14} strokeWidth={1.5} aria-hidden /> Remove
          </Button>
        )}
      </div>

      <p className="text-caption text-gray-mid mt-space-2 max-w-reading">
        PDF only, up to 5 MB. The limit is small because the brochure is emailed to the
        applicant with their prequalification letter and deal sheet. Replacing it updates the
        brochure for everyone immediately, including applicants who finished earlier — it is
        served live and is not snapshotted per submission.
      </p>

      {error && (
        <div className="mt-space-2 text-caption text-status-error" role="alert">
          {error}
        </div>
      )}

      {/* `text-onyx`, not `text-gray-dark` — the latter is an invented colour that
          resolves to no CSS at all (only `gray.mid` exists), so the text would
          silently inherit whatever the parent set. This notice carries the only
          remaining route to the file, so it has to be the readable primary ink. */}
      {blockedUrl && (
        <div className="mt-space-2 text-caption text-onyx" role="alert">
          Your browser blocked the preview window.{" "}
          <a
            href={blockedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gold-shadow hover:underline"
          >
            Open the brochure
          </a>
          .
        </div>
      )}

      {state.history.length > 0 && (
        <div className="mt-space-4">
          <h4 className="text-label uppercase tracking-wide text-gray-mid m-0 mb-space-2">
            Upload history
          </h4>
          <p className="text-caption text-gray-mid mt-0 mb-space-2 max-w-reading">
            Superseded uploads are kept, not deleted. Restoring one makes it the live{" "}
            {lower} brochure again.
          </p>
          <SubTable
            columns={historyColumns}
            rows={state.history}
            rowKey={(b) => b.id}
            caption={`${role} brochure upload history`}
          />
        </div>
      )}

      <ConfirmModal
        open={confirmRemove}
        title={`Withdraw the ${lower} brochure?`}
        destructive
        confirmLabel="Withdraw brochure"
        message={
          <>
            The Program Brochure card will stop appearing on the {lower} result page and no
            brochure will be attached to {lower} completion emails, starting immediately.
            <br />
            <br />
            Nothing is deleted — this upload moves into the history below and you can restore
            it at any time.
          </>
        }
        onConfirm={() => void doRemove()}
        onCancel={() => setConfirmRemove(false)}
      />

      <ConfirmModal
        open={confirmRestore !== null}
        title={`Restore this ${lower} brochure?`}
        confirmLabel="Restore"
        message={
          confirmRestore ? (
            <>
              <strong>{confirmRestore.originalFileName}</strong> ({" "}
              {formatBrochureSize(confirmRestore.sizeBytes)}, uploaded{" "}
              {dateTime(confirmRestore.uploadedAtUtc)}) becomes the live {lower} brochure.
              <br />
              <br />
              It is served to everyone immediately, including applicants who finished earlier.
              The current brochure moves into the history.
            </>
          ) : null
        }
        onConfirm={() => confirmRestore && void doRestore(confirmRestore)}
        onCancel={() => setConfirmRestore(null)}
      />
    </Card>
  );
}

/** The live upload: what it is, how big, how many pages, who put it there. */
function ActiveBrochure({
  brochure,
  uploadedBy,
  uploadedAt,
}: {
  brochure: ProgramBrochure;
  uploadedBy: string;
  uploadedAt: string;
}) {
  return (
    <div className="flex items-start gap-space-3 rounded-md border border-border-light bg-white-warm p-space-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border-light bg-white-pure">
        <FileText size={18} strokeWidth={1.5} aria-hidden className="text-gray-mid" />
      </div>
      <dl className="min-w-0 flex-1 m-0">
        <dt className="sr-only">File name</dt>
        <dd className="m-0 text-small text-onyx break-words">{brochure.originalFileName}</dd>
        <dt className="sr-only">Details</dt>
        <dd className="m-0 mt-space-1 text-caption text-gray-mid">
          {formatBrochureSize(brochure.sizeBytes)}
          {brochure.pageCount !== null && ` · ${brochure.pageCount} pages`}
          {" · uploaded "}
          {uploadedAt}
          {" by "}
          {uploadedBy}
        </dd>
      </dl>
    </div>
  );
}

/**
 * The empty state.
 *
 * It states the CONSEQUENCE, not just the absence. "No brochure" is a fact about
 * this screen; what the reader needs to know is that a document is missing from
 * the applicant's result page and from their email until they act — that is the
 * whole reason this state is worth words.
 */
function NoBrochure({ role }: { role: PrequalificationRole }) {
  const lower = role.toLowerCase();
  return (
    <div
      className="rounded-md border border-dashed border-border-light bg-white-warm p-space-3"
      data-testid={`brochure-empty-${lower}`}
    >
      <p className="m-0 text-small text-onyx">No {lower} brochure is set.</p>
      <p className="m-0 mt-space-1 text-caption text-gray-mid max-w-reading">
        The Program Brochure card does not appear on the {lower} result page, and no brochure
        is attached to {lower} completion emails, until one is uploaded. Everything else on
        that page and in that email is unaffected — the {lower} letter, deal sheet and
        analysis are all served as normal.
      </p>
    </div>
  );
}
